import { useEffect } from 'react';
import './BubbleAnimation.css';

export default function BubbleAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get positions
    const battleBg = playerEl.closest('.battle-bg') || document.querySelector('.battle-bg');
    if (!battleBg) {
      console.error('Battle background not found');
      onComplete();
      return;
    }

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentRef.current.getBoundingClientRect();
    const battleBgRect = battleBg.getBoundingClientRect();

    // Calculate positions relative to battle background
    const startX = (playerRect.left + playerRect.width / 2) - battleBgRect.left;
    const startY = (playerRect.top + playerRect.height / 2) - battleBgRect.top;
    const endX = (opponentRect.left + opponentRect.width / 2) - battleBgRect.left;
    const endY = (opponentRect.top + opponentRect.height / 2) - battleBgRect.top;
    
    const dx = endX - startX;
    const dy = endY - startY;

    // Track all created elements
    const elements = [];
    
    // Calculate position in front of the player
    const playerAngle = Math.atan2(dy, dx);
    const offsetDistance = playerRect.width * 0.6; // Position bubbles in front of player
    const bubbleSourceX = startX + Math.cos(playerAngle) * offsetDistance;
    const bubbleSourceY = startY + Math.sin(playerAngle) * offsetDistance;
    
    // Source glow effect in front of the player
    const sourceGlow = document.createElement('div');
    sourceGlow.className = 'bubble-source-glow';
    sourceGlow.style.left = `${bubbleSourceX}px`;
    sourceGlow.style.top = `${bubbleSourceY}px`;
    battleBg.appendChild(sourceGlow);
    elements.push(sourceGlow);

    // Create bubbles with staggered timing - similar to Ember animation
    const totalBubbles = 12;
    const bubbleTimers = [];
    const bubbles = [];
    
    for (let i = 0; i < totalBubbles; i++) {
      const timer = setTimeout(() => {
        // Create bubble that shoots from the player
        const bubble = document.createElement('div');
        bubble.className = 'bubble-particle';
        bubble.style.left = `${bubbleSourceX}px`;
        bubble.style.top = `${bubbleSourceY}px`;
        
        // Randomize slightly the trajectory and speed
        const spreadFactor = 15; // Increased spread for better visual effect
        const speed = 800 + Math.random() * 400; // 800-1200ms for proper travel
        
        // Calculate destination with wobble effect
        const wobbleX = endX - bubbleSourceX + (Math.random() - 0.5) * spreadFactor * 2;
        const wobbleY = endY - bubbleSourceY + (Math.random() - 0.5) * spreadFactor;
        
        // Set animation variables directly for transform-based animation
        bubble.style.setProperty('--end-x', `${wobbleX}px`);
        bubble.style.setProperty('--end-y', `${wobbleY}px`);
        bubble.style.setProperty('--bubble-size', `${12 + Math.random() * 16}px`); // 12-28px
        bubble.style.animationDuration = `${speed}ms`;
        bubble.dataset.speed = speed; // Store for later use
        
        // Add slight rotation
        bubble.style.setProperty('--rotation', `${(Math.random() - 0.5) * 60}deg`);
        
        // Add to DOM
        battleBg.appendChild(bubble);
        elements.push(bubble);
        bubbles.push(bubble);
        
        // Handle animation end for each bubble (pop effect)
        const handleBubbleEnd = () => {
          // Create pop effect
          const pop = document.createElement('div');
          pop.className = 'bubble-pop';
          
          // Position the pop effect directly on the opponent with slight randomization
          pop.style.left = `${endX + (Math.random() - 0.5) * 30}px`;
          pop.style.top = `${endY + (Math.random() - 0.5) * 30}px`;
          battleBg.appendChild(pop);
          elements.push(pop);
          
          // Remove bubble after it hits
          if (bubble.isConnected) {
            bubble.remove();
          }
          
          // Remove pop after animation
          setTimeout(() => {
            if (pop.isConnected) {
              pop.remove();
            }
          }, 300);
          
          bubble.removeEventListener('animationend', handleBubbleEnd);
        };
        
        bubble.addEventListener('animationend', handleBubbleEnd);
      }, i * 80); // Staggered release
      
      bubbleTimers.push(timer);
    }
    
    // Flash opponent on hit
    const hitTimer = setTimeout(() => {
      // Make opponent flash
      if (opponentEl) {
        opponentEl.style.animation = 'hitFlash 0.3s ease-in-out';
        setTimeout(() => {
          if (opponentEl) {
            opponentEl.style.animation = '';
          }
        }, 300);
      }
      
      // Create splash effects
      for (let i = 0; i < 8; i++) {
        const splash = document.createElement('div');
        splash.className = 'bubble-splash';
        splash.style.left = `${endX}px`;
        splash.style.top = `${endY}px`;
        
        // Random direction
        const splashAngle = Math.random() * Math.PI * 2;
        const splashDist = 10 + Math.random() * 30;
        splash.style.setProperty('--dx', `${Math.cos(splashAngle) * splashDist}px`);
        splash.style.setProperty('--dy', `${Math.sin(splashAngle) * splashDist}px`);
        
        battleBg.appendChild(splash);
        elements.push(splash);
        
        // Remove splash after animation
        setTimeout(() => {
          if (splash.isConnected) {
            splash.remove();
          }
        }, 500);
      }
    }, 750); // Hit after most bubbles arrive
    
    // Clean up and notify parent after animation completes
    const completeTimer = setTimeout(() => {
      // Remove source glow
      if (sourceGlow.isConnected) {
        sourceGlow.remove();
      }
      
      // Call onComplete callback
      onComplete();
    }, 1500);
    
    // Cleanup function
    return () => {
      clearTimeout(hitTimer);
      clearTimeout(completeTimer);
      
      // Clear all bubble timers
      bubbleTimers.forEach(timer => clearTimeout(timer));
      
      // Remove all elements
      elements.forEach(element => {
        if (element && element.isConnected) {
          element.remove();
        }
      });
      
      // Reset any modified styles on opponent
      if (opponentEl) {
        opponentEl.style.animation = '';
      }
    };
  }, [playerRef, opponentRef, onComplete]);
  
  return null;
}


BubbleAnimation.moveType = "water";